import React from 'react';
import { useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextEditBlock from '../invoice/texteditblock';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
const _ = require('lodash');

export default function AddressInput(props) {
    const countryCodes = useSelector((state) => state.codedata.countryCodes);
    const postalCodes = useSelector((state) => state.postaldata.postalCodes);
    const address = props.address;
    const idBase = props.idBase ? props.idBase : '';
    const hideName = props.hideName;
    const fieldConfig = props.fieldConfig ? props.fieldConfig : {};
    const { t } = useTranslation();
    // Postal code lookup data only exists for Finland, so zip code/city can only be
    // suggested/validated against it for Finnish addresses. Other countries get free text fields.
    const isFinland = address.isFinland();

    const onSelect = (_tmp, elem) => {
        if (!elem) return;
        let newAddress = _.clone(address);
        newAddress.zipCode = elem.postal_code;
        newAddress.city = elem.city_name;
        props.addressChanged(newAddress);
    };

    const onBlurZipCode = (event) => {
        let newAddress = _.clone(address);
        newAddress.zipCode = event.target.value;
        props.addressChanged(newAddress);
    };

    const onBlurCity = (event) => {
        let newAddress = _.clone(address);
        newAddress.city = event.target.value;
        props.addressChanged(newAddress);
    };

    const fixFieldConfigIfNeeded = (config) => {
        if (!config) {
            return {
                showCounter: false,
                maxLength: undefined,
            };
        }
        return config;
    };

    const handleChange = (event) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;
        let newAddress = _.clone(address);

        switch (name) {
            case 'name':
                newAddress.name = value;
                break;
            case 'streetAddress':
                newAddress.streetAddress = value;
                break;
            case 'zipCode':
                newAddress.zipCode = value;
                break;
            case 'city':
                newAddress.city = value;
                break;
            case 'country':
                newAddress.country = {
                    countryId: value,
                };
                break;
            default:
                break;
        }
        props.addressChanged(newAddress);
    };

    fieldConfig['name'] = fixFieldConfigIfNeeded(fieldConfig['name']);
    fieldConfig['streetAddress'] = fixFieldConfigIfNeeded(fieldConfig['streetAddress']);
    fieldConfig['zipCode'] = fixFieldConfigIfNeeded(fieldConfig['zipCode']);
    fieldConfig['city'] = fixFieldConfigIfNeeded(fieldConfig['city']);

    return (
        <Grid container data-testid={idBase + 'addressinput-container'}>
            <input style={{ display: 'none' }} type="text" name="fakestreetAddress" />
            <input style={{ display: 'none' }} type="text" name="fakezipCode" />
            <input style={{ display: 'none' }} type="text" name="fakecity" />
            {!hideName && (
                <TextEditBlock
                    id={idBase + 'name'}
                    name="name"
                    label={t('general.name')}
                    value={address.name ? address.name : ''}
                    onChange={handleChange}
                    number={false}
                    required={false}
                    showCounter={fieldConfig['name'].showCounter}
                    maxLength={fieldConfig['name'].maxLength}
                />
            )}

            <TextEditBlock
                id={idBase + 'streetAddress'}
                name="streetAddress"
                label={t('general.streetAddressOrPoPox')}
                value={address.streetAddress ? address.streetAddress : ''}
                onChange={handleChange}
                required={true}
                number={false}
                showCounter={fieldConfig['streetAddress'].showCounter}
                maxLength={fieldConfig['streetAddress'].maxLength}
            />

            {isFinland ? (
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={postalCodes ? postalCodes : []}
                        getOptionLabel={(option) => (option ? `${option.postal_code}` : '')}
                        renderOption={(renderProps, option) => (
                            <span {...renderProps}>
                                {option.postal_code} {option.city_name}
                            </span>
                        )}
                        filterOptions={createFilterOptions({
                            stringify: (option) => option.postal_code + option.city_name,
                        })}
                        id={idBase + 'zipCode'}
                        name="zipCode"
                        loading={!postalCodes}
                        freeSolo
                        loadingText={t('general.loading')}
                        onChange={onSelect}
                        value={address.zipCode ? { postal_code: address.zipCode } : { postal_code: '' }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label={t('general.zipCode')}
                                margin={'normal'}
                                name="zipCode"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password',
                                }}
                                error={!address.zipCode || address.zipCode.length !== 5}
                                sx={{ mx: 1, width: '95%' }}
                                onBlur={(e) => onBlurZipCode(e)}
                            />
                        )}
                    />
                </Grid>
            ) : (
                <TextEditBlock
                    id={idBase + 'zipCode'}
                    name="zipCode"
                    label={t('general.zipCode')}
                    value={address.zipCode ? address.zipCode : ''}
                    onChange={handleChange}
                    onBlur={onBlurZipCode}
                    number={false}
                    required={true}
                    showCounter={fieldConfig['zipCode'].showCounter}
                    maxLength={fieldConfig['zipCode'].maxLength}
                />
            )}

            {isFinland ? (
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={postalCodes ? postalCodes : []}
                        getOptionLabel={(option) => (option ? `${option.city_name}` : '')}
                        renderOption={(renderProps, option) => (
                            <span {...renderProps}>
                                {option.postal_code} {option.city_name}
                            </span>
                        )}
                        filterOptions={createFilterOptions({
                            stringify: (option) => option.postal_code + option.city_name,
                        })}
                        id={idBase + 'city'}
                        name="city"
                        loading={!postalCodes}
                        freeSolo
                        loadingText={t('general.loading')}
                        onChange={onSelect}
                        value={address.city ? { city_name: address.city } : { city_name: '' }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label={t('general.city')}
                                margin={'normal'}
                                name="city"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password',
                                }}
                                error={!address.city || address.city.length === 0}
                                sx={{ mx: 1, width: '95%' }}
                                onBlur={(e) => onBlurCity(e)}
                            />
                        )}
                    />
                </Grid>
            ) : (
                <TextEditBlock
                    id={idBase + 'city'}
                    name="city"
                    label={t('general.city')}
                    value={address.city ? address.city : ''}
                    onChange={handleChange}
                    onBlur={onBlurCity}
                    number={false}
                    required={true}
                    showCounter={fieldConfig['city'].showCounter}
                    maxLength={fieldConfig['city'].maxLength}
                />
            )}

            <Grid item xs={12} md={6}>
                <FormControl sx={{ mt: 2, mb: 1, mx: 1, width: '95%' }}>
                    <InputLabel id="country-label">{t('general.country')}</InputLabel>
                    <Select
                        required
                        labelId="country-label"
                        id={idBase + 'country'}
                        name="country"
                        value={address.country.countryId}
                        onChange={handleChange}>
                        {countryCodes.map((code) => (
                            <MenuItem key={code.countryId} value={code.countryId}>
                                {code.texts[0].text} ({code.countryShortStr2})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {!address.isValid() && (
                <Grid item xs={12}>
                    <Alert severity="error">{t('general.addressInvalid')}</Alert>
                </Grid>
            )}
        </Grid>
    );
}

AddressInput.propTypes = {
    address: PropTypes.object,
    idBase: PropTypes.string,
    hideName: PropTypes.bool,
    fieldConfig: PropTypes.object,
    required: PropTypes.bool,
};
